import { useContentMessages } from '@/features/editor/hooks/useContentMessages'
import { useStrictLang } from './design/strictLangContext'
import type { QuizBlock } from '@/types/blocks'
import type { ContentLang } from '@/features/editor/editorSlice'
import { QuizShell, blockTheme } from '@teach/ui'
import { yesNoValue } from '../../preview/answerState'
import type { PreviewRun } from '../../preview/previewRun'
import { getDisplayBody } from '../quizBodySync'
import { AnswerFeedback } from './AnswerFeedback'
import { PreviewShell } from './PreviewShell'
import { useQuizMascot } from './quizMascotContext'
import { quizFeedback } from './quizFeedback'
import { YesNoOption } from './YesNoOption'
import { pickMessages, useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  block: QuizBlock
  contentLang: ContentLang
  /** Прохождение: нажатие СРАЗУ отвечает — у yes_no в плеере нет кнопки проверки. */
  run?: PreviewRun
}

const OPTIONS = [
  { value: true, variant: 'yes' as const },
  { value: false, variant: 'no' as const },
]

export const YesNoQuizPreview = ({ block, contentLang, run }: Props) => {
  const m = useMessages(messages)
  // Подсказки полей — на языке правки: видно, что правится английская версия.
  const cm = useContentMessages(messages)
  // Холст и миниатюры редактора — строго язык правки; прохождение — как плеер.
  const strictLang = useStrictLang() && (!run || run.onCanvas === true)
  const mascot = useQuizMascot(run?.verdict)
  const statement = getDisplayBody(block, contentLang, strictLang).question ?? ''
  const picked = run ? yesNoValue(run.answer) : null

  return (
    <PreviewShell
      // До ответа нижней кнопки нет вовсе (`ctaLabel` пуст) — ровно как в
      // плеере: `YesNoBlock` не передаёт `onSubmit` в футер.
      cta={run?.ctaLabel ?? undefined}
      ctaState={run?.ctaState}
      onCta={run?.onCta}
      // Место под кнопкой — и до ответа: её появление не сдвигает экран вверх.
      reserveCta={run !== undefined}
      style={{ backgroundColor: blockTheme.quiz.bg }}
      contentClassName="min-h-0"
      verdictPlate={
        run ? (
          <AnswerFeedback
            lang={contentLang}
            verdict={run.verdict}
            text={quizFeedback(block, contentLang, run.verdict)}
          />
        ) : undefined
      }
    >
      <QuizShell
        question={statement}
        maxQuestionLines={3}
        mascot={mascot}
        placeholder={run && !run.onCanvas ? undefined : cm.statementPlaceholder}
      >
        {/* Два круга в ряд по центру, зазор 48 — как в мобилке. */}
        <div className="mx-auto flex justify-center gap-12">
          {OPTIONS.map(({ value, variant }) => (
            <YesNoOption
              key={variant}
              label={m[variant]}
              variant={variant}
              dimmed={picked === null ? false : picked !== value}
              disabled={picked !== null}
              onSelect={run ? () => run.setAnswer({ kind: 'yesNo', value }) : undefined}
            />
          ))}
        </div>
      </QuizShell>
    </PreviewShell>
  )
}
